import { useCallback, useRef, useState, type KeyboardEvent, type RefObject } from "react";
import { useStore } from "react-redux";

import { agentClient } from "../agentClient";
import { takeBackThreadFile, useAui } from "../aui";
import type { MentionCandidate } from "../hooks/useMentionCandidates";
import type { AgentState, OutgoingFile } from "../store/agentSlice";
import type { ThreadFileInfo } from "./files";
import {
  findMentions,
  lostMentions,
  mentionText,
  mentionToDelete,
  renameMentions,
  resolveMention,
  snapSelection,
  stripMentions,
  type MentionMatch,
} from "./mention";
import { useAddFiles } from "./useAddFiles";
import { useThreadId } from "./useThreadId";

type State = { agent: AgentState };
type Aui = ReturnType<typeof useAui>;

const NO_FILES: OutgoingFile[] = [];
const NO_NAMES: readonly string[] = [];

function draftFiles(state: State, threadId: number | null): OutgoingFile[] {
  return threadId == null ? NO_FILES : (state.agent.threads[threadId]?.composer.files ?? NO_FILES);
}

/** Writes the draft's text from here, not by typing: this composer and every other view. */
function writeDraft(aui: Aui, threadId: number, text: string) {
  aui.composer.setText(text);
  agentClient.editComposer(threadId, text);
}

/**
 * The mentions of a file going off the draft go with it, unless another file of its name stays
 * (they then mean that one). For the composer's attachment chips.
 */
export function useDropMentions() {
  const aui = useAui();
  const store = useStore<State>();
  const threadId = useThreadId();
  return useCallback(
    (name: string, fileId: number | null) => {
      if (threadId == null) return;
      const files = draftFiles(store.getState(), threadId);
      if (
        resolveMention(
          files.filter((file) => file.id !== fileId),
          name
        )
      )
        return;
      const names = [...files.map((file) => file.name), name];
      const text = stripMentions(aui.composer.getState().text, names, name);
      if (text != null) writeDraft(aui, threadId, text);
    },
    [aui, store, threadId]
  );
}

/**
 * The composer's side of @ mentions: a pick writes "@Name " where the "@query" was and puts the
 * file on the draft once; a mention is one atom (the caret never rests inside it, Backspace and
 * Delete take it whole); and the last mention of a file going takes the file off the draft.
 * Edits go through the textarea's own editing (execCommand), so the browser's undo has them;
 * where that is missing they are written like a remote edit.
 */
export function useComposerMentions(
  threadId: number | null,
  input: RefObject<HTMLTextAreaElement | null>
) {
  const aui = useAui();
  const store = useStore<State>();
  const { addThreadFile, addDocument, addWorkspace } = useAddFiles();
  // Documents being linked (and Files panel files being shared), one name per pick: their mentions
  // show as pending until the file is on the draft, and the message waits for them.
  const [pending, setPending] = useState<readonly string[]>(NO_NAMES);
  const pendingNames = useRef<readonly string[]>(NO_NAMES);
  const caret = useRef<number | null>(null);
  // While a pick writes its mention: that input event is not typing, so a notice its add gives
  // ("… is already on your message.", which can land before the edit) stays.
  const picking = useRef(false);
  // The text when an IME composition began: what its outcome is compared with, once it ends.
  const composedFrom = useRef<string | null>(null);
  const shownThread = useRef(threadId);
  shownThread.current = threadId;

  const files = useCallback(() => draftFiles(store.getState(), threadId), [store, threadId]);
  const mentionsIn = (text: string) =>
    findMentions(text, [...files().map((file) => file.name), ...pendingNames.current]);

  const setPendingNames = (next: readonly string[]) => {
    pendingNames.current = next;
    setPending(next);
  };

  /** Takes off the draft each file whose last mention the edit from `before` to `after` removed. */
  const dropUnmentioned = (before: string, after: string) => {
    if (threadId == null) return;
    const held = files();
    for (const name of lostMentions(
      before,
      after,
      held.map((file) => file.name)
    )) {
      const file = resolveMention(held, name);
      if (file) void takeBackThreadFile(threadId, file.id);
    }
  };

  /**
   * Writes `insert` over [start, end) as the browser's own edit, after the event that asked for it:
   * an edit inside a React handler is re-rendered over before the composer has its text, which
   * loses it from the browser's undo.
   */
  const replace = (
    element: HTMLTextAreaElement,
    start: number,
    end: number,
    insert: string,
    picked = false
  ) => {
    queueMicrotask(() => edit(element, start, end, insert, picked));
  };

  const edit = (
    element: HTMLTextAreaElement,
    start: number,
    end: number,
    insert: string,
    picked: boolean
  ) => {
    element.focus();
    element.setSelectionRange(start, end);
    const command = insert ? "insertText" : "delete";
    // Fires the input event: onChange takes it from there, as for typing.
    picking.current = picked;
    try {
      if (
        typeof document.execCommand === "function" &&
        document.execCommand(command, false, insert)
      )
        return;
    } finally {
      picking.current = false;
    }
    if (threadId == null) return;
    const before = element.value;
    const after = before.slice(0, start) + insert + before.slice(end);
    dropUnmentioned(before, after);
    writeDraft(aui, threadId, after);
    const at = start + insert.length;
    requestAnimationFrame(() => element.setSelectionRange(at, at));
  };

  /**
   * One pick's link answered: its mentions get the file's own name, or go if nothing else can
   * still back them (another pick of the name in flight, or a file of that name on the draft).
   */
  const settle = (thread: number, name: string, file: ThreadFileInfo | null) => {
    const rest = [...pendingNames.current];
    rest.splice(rest.indexOf(name), 1);
    setPendingNames(rest);
    if (shownThread.current !== thread) return;
    const held = files();
    const names = [...held.map((other) => other.name), ...rest, name];
    const text = aui.composer.getState().text;
    const mentioned = (wanted: string) =>
      findMentions(text, names).some((token) => token.name === wanted);
    if (!file) {
      if (rest.includes(name) || resolveMention(held, name)) return;
      const stripped = stripMentions(text, names, name);
      if (stripped != null) writeDraft(aui, thread, stripped);
    } else if (!mentioned(name) && !mentioned(file.name)) {
      void takeBackThreadFile(thread, file.id); // its mentions were deleted meanwhile
    } else if (file.name !== name && mentioned(name)) {
      const renamed = renameMentions(text, names, name, file.name);
      if (renamed != null) writeDraft(aui, thread, renamed);
    }
  };

  const pick = (match: MentionMatch, candidate: MentionCandidate) => {
    const element = input.current;
    if (!element || threadId == null) return;
    if (candidate.kind === "thread_file") {
      // First, so the mention shows as one at once.
      addThreadFile({ id: candidate.id, name: candidate.name, contentType: candidate.contentType });
    } else {
      setPendingNames([...pendingNames.current, candidate.name]);
      const thread = threadId;
      const added =
        candidate.kind === "workspace_file"
          ? addWorkspace({ ...candidate, path: candidate.file.path })
          : addDocument(candidate);
      void added.then((file) => settle(thread, candidate.name, file));
    }
    replace(element, match.start, match.end, `${mentionText(candidate.name)} `, true);
  };

  /**
   * Call from the input's onChange, before the composer takes the new text. `composing`: the
   * input event's isComposing.
   */
  const onChange = (element: HTMLTextAreaElement, composing: boolean) => {
    if (composing && composedFrom.current != null) return; // settled when the composition ends
    endLostComposition();
    const before = aui.composer.getState().text;
    const after = element.value;
    // After the Input's own handler, as useComposerSync's write: a store write before it re-renders
    // the textarea with the old text, which also wipes the browser's undo of this edit.
    queueMicrotask(() => dropUnmentioned(before, after));
  };

  /**
   * An IME composition's text is provisional: mentions it breaks keep their files until it ends,
   * and a cancelled one (the text as it began) changes nothing.
   */
  /**
   * Input that is not composing means no composition is open, whatever we last heard: writing the
   * textarea's value (a remote edit) ends one in Chromium with no compositionend.
   */
  const endLostComposition = () => {
    composedFrom.current = null;
  };
  const onCompositionStart = () => {
    composedFrom.current = aui.composer.getState().text;
  };
  const onCompositionEnd = (element: HTMLTextAreaElement) => {
    const before = composedFrom.current;
    composedFrom.current = null;
    const after = element.value;
    if (before != null) queueMicrotask(() => dropUnmentioned(before, after));
  };

  /** Keeps the selection off the inside of a mention. Call from the input's onSelect. */
  const onSelect = (element: HTMLTextAreaElement) => {
    if (composedFrom.current != null) return; // moving it would end the composition
    const { selectionStart: start, selectionEnd: end, selectionDirection } = element;
    const [from, to] = snapSelection(mentionsIn(element.value), start, end, caret.current);
    if (from !== start || to !== end) element.setSelectionRange(from, to, selectionDirection);
    caret.current = selectionDirection === "backward" ? from : to;
  };

  /** Backspace or Delete next to a mention takes it whole; true when it did. */
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): boolean => {
    if (event.nativeEvent.isComposing) return false;
    endLostComposition();
    if (event.key !== "Backspace" && event.key !== "Delete") return false;
    const element = event.currentTarget;
    if (element.selectionStart !== element.selectionEnd) return false;
    const token = mentionToDelete(mentionsIn(element.value), element.selectionStart, event.key);
    if (!token) return false;
    event.preventDefault();
    replace(element, token.start, token.end, "");
    return true;
  };

  /** Whether the "@" at `start` begins a finished mention (no list for it). */
  const isMention = (text: string, start: number) =>
    mentionsIn(text).some((token) => token.start === start);

  return {
    pending,
    pick,
    /** The input event under way is a pick writing its mention, not typing. */
    isPickEdit: () => picking.current,
    /** An IME composition is open: nothing of ours renders until it ends. */
    isComposing: () => composedFrom.current != null,
    onChange,
    onSelect,
    onKeyDown,
    onCompositionStart,
    onCompositionEnd,
    isMention,
  };
}
